interface ClientI {
  <T>(
    endpoint: string,
    type?: 'GET' | 'POST' | 'PUT' | 'DELETE',
    options?: { isAuthRequired?: boolean; data?: any; isMultipartForm?: boolean }
  ): Promise<T>;
}

interface VendorAppNotification {
  id: number;
  vendor_id: number;
  booking_id: number | null;
  user_id: number;
  vendor_client_base_id: number;
  type:
    | 'booking_confirmed'
    | 'booking_cancelled'
    | 'booking_refunded'
    | 'booking_rescheduled'
    | 'ig_screenshots_ready'
    | 'daily_brief'
    | 'community_question'
    | 'community_report'
    | 'community_update_posted'
    | 'review_received'
    | 'review_private_note'
    // Ficha de Google conectada: llegó una reseña nueva en Google.
    | 'google_review_received'
    // Al profesional del equipo (en SU campanita): una reseña de un turno que atendió, de Ágora o
    // de Google (emparejada con nuestro pedido de reseña).
    | 'review_for_professional'
    | 'product_low_stock'
    | 'gift_card_purchased'
    | 'community_member_joined'
    // Tu mes en historias: «Tu octubre está listo» el 2, «Tu 2026 está listo» el 1 de diciembre
    // (`data.kind` + `data.period`). Las filas viejas de Resúmenes mensuales no traen `kind`.
    | 'wrapped'
    // Retirados (Pulse y WA Bot): pueden quedar filas viejas en la base;
    // isApiNotificationVisible las oculta para que no lleven a una ruta que ya no existe.
    | 'pulse'
    | 'wa_human_handoff'
    // Fichaje del equipo: alguien no marcó su llegada antes de su primer turno, y PIN de
    // recepción equivocado tres veces en la pantalla de fichaje.
    | 'attendance_missing'
    | 'attendance_pin_locked'
    // Premios: una clienta llegó a un premio, lo usó, o tiene turno mañana con un premio esperando.
    | 'premio_won'
    | 'premio_used'
    | 'premio_tomorrow'
    // Mercado Pago por profesional: al dueño, un profesional desconectó su cuenta propia (o Mercado
    // Pago dejó de aceptarla); al profesional, el dueño desconectó la suya.
    | 'mp_professional_disconnected'
    | 'mp_own_account_disconnected'
    // Conversaciones (beta): mensaje nuevo (agrupado por conversación), aviso del canal de WhatsApp
    // (`data.kind`) y cambio de estado de una plantilla en la revisión de Meta.
    | 'conversation_message'
    | 'conversation_channel'
    | 'whatsapp_template_status';
  data: {
    // Tu mes en historias (wrapped): qué está listo y su período, `YYYY-MM` o `YYYY`.
    // Conversaciones: aviso del canal (conversation_channel).
    kind?: 'tu_mes' | 'tu_anio' | 'app_inactive' | 'disconnected' | 'history_done';
    days?: number;
    period?: string;
    headline?: string;
    // Booking fields (present for booking_* types)
    bookingId?: number;
    customerName?: string;
    serviceName?: string;
    eventDate?: string;
    eventTime?: string;
    oldEventDate?: string;
    oldEventTime?: string;
    newEventDate?: string;
    newEventTime?: string;
    amount?: number;
    paymentType?: string;
    createdAt?: Date;
    /** booking_confirmed (beta de reservas online): reservó con el teléfono de un cliente
     *  restringido. Ya viene redactado («Usó el teléfono de María López, que tiene…»). */
    restricted_contact_warning?: string;
    // System fields (present for ig_screenshots_ready)
    jobId?: number;
    slidesCompleted?: number;
    slidesTotal?: number;
    // Daily brief fields (present for type 'daily_brief')
    brief_date?: string;
    items?: unknown[];
    // Community fields (present for community_* types)
    post_id?: number;
    space_id?: number;
    space_name?: string;
    author_name?: string;
    reporter_name?: string;
    excerpt?: string;
    // Card de novedad del catálogo (community_update_posted)
    asset_type?: 'online_course' | 'unique_event';
    asset_id?: number;
    // Reseñas (review_received / review_private_note). `excerpt` se comparte con Comunidad.
    review_id?: number;
    booking_id?: number | null;
    rating?: number | null;
    client_name?: string;
    service_name?: string | null;
    staff_name?: string | null;
    visibility?: 'public' | 'private';
    has_private_note?: boolean;
    // Reseña nueva en Google (google_review_received). Sin nombre ni texto: Google no deja
    // guardarlos más de 30 días, así que el aviso solo trae el id y las estrellas.
    google_review_id?: number;
    // El cliente EDITÓ la reseña y cambiaron las estrellas: cuántas tenía antes (`rating` = las de ahora).
    previous_rating?: number;
    // Llegó por nuestro pedido de reseña: el turno y quién atendió son datos nuestros (`booking_id`, arriba).
    from_request?: boolean;
    staff_first_name?: string | null;
    // Reseña de un turno suyo, para el profesional (review_for_professional). `customerName` es el
    // nombre de pila del cliente (falta si no puede ver nombres); `excerpt` solo en las de Ágora.
    source?: 'agora' | 'google';
    // Stock bajo (product_low_stock)
    productId?: number;
    productName?: string;
    remaining?: number;
    isOutOfStock?: boolean;
    // Tarjeta de regalo vendida online (gift_card_purchased). customerName/amount, arriba.
    giftCardId?: number;
    recipientName?: string;
    // Alta en la comunidad (community_member_joined).
    subscription_id?: number;
    member_name?: string;
    membership_name?: string;
    amount_label?: string;
    // Fichaje del equipo (attendance_missing / attendance_pin_locked).
    vendor_id?: number;
    name?: string;
    first_booking_at?: string | null;
    work_date?: string;
    venue_id?: number | null;
    venue_name?: string | null;
    // Premios (premio_won / premio_used / premio_tomorrow). client_name, booking_id y amount, arriba.
    user_id?: number;
    premio_title?: string;
    points?: number;
    start_at?: string;
    // Mercado Pago por profesional (mp_professional_disconnected / mp_own_account_disconnected).
    professional_vendor_id?: number;
    professional_name?: string;
    business_name?: string;
    // En conversation_channel: 'partner_removed' | 'app_inactive' | 'token'; en whatsapp_template_status, el
    // motivo del rechazo de Meta en texto.
    reason?: 'owner' | 'professional' | 'mercado_pago' | (string & {});
    // Conversaciones (conversation_message): `name` y `excerpt`, arriba.
    conversation_id?: number;
    preview?: string;
    count?: number;
    channel?: 'web' | 'whatsapp';
    known?: boolean; // sin nombre ni ficha: avatar gris
    // Plantilla de WhatsApp (whatsapp_template_status). `name`, arriba.
    template_id?: number;
    status?: 'approved' | 'rejected' | 'paused' | 'disabled' | 'pending';
  };
  read: boolean;
  archived: boolean;
  created_at: Date;
  updated_at?: Date;
  user?: {
    id: number;
    name: string;
    email: string;
    phone?: string;
  };
  vendor_client_base?: {
    id: number;
    user_id: number;
    vendor_id: number;
    phone?: string;
  };
}

interface NotificationResponse {
  success: boolean;
  data: {
    notifications: VendorAppNotification[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      unreadCount: number;
    };
  };
}

interface NotificationActionResponse {
  success: boolean;
  message: string;
}

export const createNotificationService = (client: ClientI) => ({
  async fetchNotifications(page = 1, limit = 50): Promise<NotificationResponse> {
    return await client<NotificationResponse>(
      `me/vendor/app-notifications?page=${page}&limit=${limit}`,
      'GET',
      { isAuthRequired: true }
    );
  },

  async markAsRead(notificationId: number): Promise<NotificationActionResponse> {
    return await client<NotificationActionResponse>(
      `me/vendor/app-notifications/${notificationId}/read`,
      'PUT',
      { isAuthRequired: true }
    );
  },

  async markAllAsRead(): Promise<NotificationActionResponse> {
    return await client<NotificationActionResponse>(`me/vendor/app-notifications/mark-all-read`, 'PUT', {
      isAuthRequired: true
    });
  },

  async archiveNotification(notificationId: number): Promise<NotificationActionResponse> {
    return await client<NotificationActionResponse>(
      `me/vendor/app-notifications/${notificationId}/archive`,
      'PUT',
      { isAuthRequired: true }
    );
  }
});
export type { NotificationActionResponse, NotificationResponse, VendorAppNotification };
